import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { EnrichedMatch } from '../types';
import { relativeKickoff, rigaTime, stageLabel } from '../format';
import { fmtOdds, impliedProbs } from '../odds';
import { useAuth } from '../auth';
import ProposeBetSheet from './ProposeBetSheet';
import { getMatch } from '../api';

function useScoreTick(score: number | null): string {
  const prev = useRef<number | null>(score);
  const [tick, setTick] = useState(false);
  useEffect(() => {
    if (score !== null && prev.current !== null && score > prev.current) {
      setTick(true);
      const t = setTimeout(() => setTick(false), 900);
      return () => clearTimeout(t);
    }
    prev.current = score;
  }, [score]);
  return tick ? 'animate-score-tick' : '';
}

interface Props {
  match: EnrichedMatch;
  highlightCodes?: string[];
  showOwners?: boolean;
}

export default function MatchCard({ match: m, highlightCodes, showOwners }: Props) {
  const live = m.status === 'in';
  const done = m.status === 'post';
  const homeWin = m.winner === 'home';
  const awayWin = m.winner === 'away';
  const hl = highlightCodes ?? [];
  const isHomeMine = hl.includes(m.home_team);
  const isAwayMine = hl.includes(m.away_team);
  const probs = impliedProbs(m);
  const { user } = useAuth();
  const qc = useQueryClient();
  const [betOpen, setBetOpen] = useState(false);
  const canBet = !!user && !done;

  function prefetch() {
    qc.prefetchQuery({
      queryKey: ['match', m.id],
      queryFn: () => getMatch(m.id),
      staleTime: 30_000,
    });
  }

  return (
    <article
      className={`card p-3 sm:p-4 ${live ? 'ring-1 ring-pitch-500/40 shadow-glow' : ''}`}
    >
      <Link to={`/matches/${m.id}`} className="block group" onMouseEnter={prefetch} onTouchStart={prefetch}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <Side
            flag={m.home_flag}
            name={m.home_name}
            owner={showOwners && !isHomeMine ? m.home_owner : undefined}
            score={m.home_score}
            pk={m.home_pk_score}
            isWinner={homeWin}
            dim={done && !homeWin}
            mine={isHomeMine}
          />
          <Side
            flag={m.away_flag}
            name={m.away_name}
            owner={showOwners && !isAwayMine ? m.away_owner : undefined}
            score={m.away_score}
            pk={m.away_pk_score}
            isWinner={awayWin}
            dim={done && !awayWin}
            mine={isAwayMine}
          />
        </div>
        <div className="flex flex-col items-end gap-1 text-right text-xs shrink-0 w-20">
          <span className="pill bg-ink-700/60 text-dim">{stageLabel(m.stage)}</span>
          {live ? (
            <span className="pill bg-pitch-700/30 text-pitch-400">
              <span className="size-1.5 rounded-full bg-pitch-400 animate-pulse" />
              {m.status_detail ?? 'LIVE'}
            </span>
          ) : done ? (
            <span className="text-dim">
              {m.ended_phase === 'ot' ? 'AET' : m.ended_phase === 'pk' ? 'Pens.' : 'FT'}
            </span>
          ) : (
            <>
              <span className="font-mono tabular-nums text-chalk text-sm">{rigaTime(m.utc_kickoff)}</span>
              <span className="text-dim">{relativeKickoff(m.utc_kickoff)}</span>
            </>
          )}
        </div>
      </div>

      {!done && (m.ml_home || m.ml_draw || m.ml_away) && (
        <OddsRow m={m} probs={probs} />
      )}
      </Link>

      {canBet && (
        <div className="mt-3 pt-3 border-t border-ink-800">
          <button
            onClick={() => setBetOpen(true)}
            className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-ink-800 text-pitch-400 text-xs font-medium hover:bg-ink-700 active:bg-ink-600"
          >
            <Bolt /> Propose a bet on this match
          </button>
        </div>
      )}

      <ProposeBetSheet
        open={betOpen}
        onClose={() => setBetOpen(false)}
        initialMatch={m}
      />
    </article>
  );
}

function Bolt() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

function Side({
  flag, name, owner, score, pk, isWinner, dim, mine,
}: {
  flag: string | null | undefined;
  name: string;
  owner?: string | null;
  score: number | null;
  pk: number | null;
  isWinner: boolean;
  dim: boolean;
  mine: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 ${dim ? 'opacity-60' : ''}`}>
      <span className="text-lg leading-none w-7 text-center">{flag ?? '🏳️'}</span>
      <div className={`flex-1 min-w-0 truncate ${isWinner ? 'font-semibold text-chalk' : 'text-chalk/90'} ${mine ? 'underline decoration-pitch-500/60 decoration-2 underline-offset-4' : ''}`}>
        {name}
        {owner != null && (
          <span className="ml-2 text-[10px] uppercase tracking-wider text-dim">
            {owner}
          </span>
        )}
      </div>
      {score !== null ? (
        <div className="stat text-lg w-8 text-right flex items-baseline justify-end gap-1">
          <ScoreNum n={score} />
          {pk !== null && <span className="text-xs text-dim">({pk})</span>}
        </div>
      ) : null}
    </div>
  );
}

function OddsRow({ m, probs }: { m: EnrichedMatch; probs: ReturnType<typeof impliedProbs> }) {
  const ph = probs ? Math.round(probs.home * 100) : null;
  const px = probs ? Math.round(probs.draw * 100) : null;
  const pa = probs ? Math.round(probs.away * 100) : null;

  return (
    <div className="mt-3 pt-3 border-t border-ink-800">
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <OddsCell label="1" coef={m.ml_home} pct={ph} />
        <OddsCell label="X" coef={m.ml_draw} pct={px} />
        <OddsCell label="2" coef={m.ml_away} pct={pa} />
      </div>
      {probs && (
        <div className="mt-2 flex h-1.5 rounded-full overflow-hidden bg-ink-800">
          <div className="bg-pitch-500/70" style={{ width: `${probs.home * 100}%` }} />
          <div className="bg-ink-600" style={{ width: `${probs.draw * 100}%` }} />
          <div className="bg-pitch-700/70" style={{ width: `${probs.away * 100}%` }} />
        </div>
      )}
      {(m.over_under != null || m.odds_provider) && (
        <div className="mt-2 flex items-center justify-between text-[10px] text-dim">
          <span>{m.over_under != null ? `O/U ${m.over_under}` : ''}</span>
          <span>{m.odds_provider ?? ''}</span>
        </div>
      )}
    </div>
  );
}

function ScoreNum({ n }: { n: number }) {
  const cls = useScoreTick(n);
  return <span className={`inline-block ${cls}`}>{n}</span>;
}

function OddsCell({ label, coef, pct }: { label: string; coef: number | null; pct: number | null }) {
  return (
    <div className="rounded-md bg-ink-800/80 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-dim">{label}</div>
      <div className="stat text-sm text-chalk">{fmtOdds(coef)}</div>
      {pct != null && <div className="text-[10px] text-pitch-400 stat">{pct}%</div>}
    </div>
  );
}
